import { useState, useEffect, useCallback } from "react";
import { supabase } from "../../../lib/supabase";
import { useAuth } from "../../../contexts/AuthContext";

interface Props { workspaceId: string; }

interface Presentation {
  id: string;
  user_id: string;
  title: string;
  summary: string;
  achievements: string;
  challenges: string;
  learnings: string;
  status: "draft" | "submitted" | "reviewed";
  admin_score: number | null;
  admin_feedback: string | null;
  submitted_at: string | null;
  reviewed_at: string | null;
  display_name?: string;
  email?: string;
}

const SCORES = [
  { value: 5, grade: "A+", label: "최우수", color: "#00C896" },
  { value: 4, grade: "A",  label: "우수",   color: "#7A9BFF" },
  { value: 3, grade: "B",  label: "양호",   color: "#FF9F43" },
  { value: 2, grade: "C",  label: "보통",   color: "#E8375F" },
  { value: 1, grade: "D",  label: "미흡",   color: "#888888" },
];

const STATUS_CFG = {
  draft:     { label: "작성 중",   color: "#555555", icon: "ri-draft-line" },
  submitted: { label: "검토 대기", color: "#FF9F43", icon: "ri-time-line" },
  reviewed:  { label: "평가 완료", color: "#00C896", icon: "ri-checkbox-circle-fill" },
};

export default function FinalPresentationManager({ workspaceId }: Props) {
  const { user } = useAuth();
  const [list, setList] = useState<Presentation[]>([]);
  const [selected, setSelected] = useState<Presentation | null>(null);
  const [loading, setLoading] = useState(true);
  const [score, setScore] = useState<number>(4);
  const [feedback, setFeedback] = useState("");
  const [saving, setSaving] = useState(false);
  const [filterStatus, setFilterStatus] = useState<"all" | "submitted" | "reviewed">("submitted");
  const [showTeamKPIForm, setShowTeamKPIForm] = useState(false);
  const [newKPI, setNewKPI] = useState({ title: "", description: "", target_value: "", unit: "", category: "tasks", deadline: "" });
  const [savingKPI, setSavingKPI] = useState(false);

  const fetchData = useCallback(async () => {
    if (!workspaceId) return;
    setLoading(true);
    const { data } = await supabase.from("final_presentations").select("*").eq("workspace_id", workspaceId).order("submitted_at", { ascending: false });
    if (!data?.length) { setList([]); setLoading(false); return; }
    const uids = [...new Set(data.map((d: Presentation) => d.user_id))];
    const { data: profiles } = await supabase.from("profiles").select("id,display_name,email").in("id", uids);
    const pmap: Record<string, { display_name: string; email: string }> = {};
    (profiles ?? []).forEach((p: { id: string; display_name: string; email: string }) => { pmap[p.id] = p; });
    setList(data.map((d: Presentation) => ({ ...d, display_name: pmap[d.user_id]?.display_name ?? "익명", email: pmap[d.user_id]?.email ?? "-" })));
    setLoading(false);
  }, [workspaceId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleReview = async () => {
    if (!selected || saving || !user) return;
    setSaving(true);
    await supabase.from("final_presentations").update({ status: "reviewed", admin_score: score, admin_feedback: feedback.trim() || null, reviewed_at: new Date().toISOString(), reviewed_by: user.id }).eq("id", selected.id);
    setSaving(false);
    setSelected(null);
    setFeedback("");
    await fetchData();
  };

  const handleAddTeamKPI = async () => {
    if (!workspaceId || !user || !newKPI.title.trim() || !newKPI.target_value) return;
    setSavingKPI(true);
    await supabase.from("team_kpi_goals").insert({
      workspace_id: workspaceId,
      title: newKPI.title.trim(),
      description: newKPI.description.trim() || null,
      target_value: parseFloat(newKPI.target_value),
      unit: newKPI.unit.trim(),
      category: newKPI.category,
      deadline: newKPI.deadline || null,
      created_by: user.id,
    });
    setSavingKPI(false);
    setShowTeamKPIForm(false);
    setNewKPI({ title: "", description: "", target_value: "", unit: "", category: "tasks", deadline: "" });
  };

  const filtered = list.filter(p => filterStatus === "all" || p.status === filterStatus);
  const submittedCnt = list.filter(p => p.status === "submitted").length;
  const reviewedCnt = list.filter(p => p.status === "reviewed").length;

  return (
    <div className="space-y-5">
      {/* 팀 KPI 목표 추가 */}
      <div className="bg-[#111318] rounded-xl border border-[#252830] p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <i className="ri-team-line text-[#A78BFA] text-base"></i>
            <span className="text-white text-sm font-semibold">팀 공동 KPI 설정</span>
            <span className="text-[#5A5E72] text-xs">학생 대시보드에 표시됩니다</span>
          </div>
          <button type="button" onClick={() => setShowTeamKPIForm(v => !v)}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-[#A78BFA]/15 border border-[#A78BFA]/30 text-[#A78BFA] text-xs rounded-lg cursor-pointer hover:bg-[#A78BFA]/25 transition-colors whitespace-nowrap">
            <i className="ri-add-line text-sm"></i>목표 추가
          </button>
        </div>
        {showTeamKPIForm && (
          <div className="bg-[#1C1F2E] rounded-xl border border-[#252830] p-4 space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[#8B91A8] text-xs mb-1 block">목표 이름</label>
                <input type="text" value={newKPI.title} onChange={e => setNewKPI(f => ({ ...f, title: e.target.value }))} maxLength={50}
                  placeholder="예: 팀 전체 태스크 100건 완료"
                  className="w-full bg-[#252830] text-white text-xs px-3 py-2 rounded-lg border border-[#2A2D3E] focus:outline-none focus:border-[#A78BFA] placeholder-[#3E4155]" />
              </div>
              <div className="flex gap-2">
                <div className="flex-1">
                  <label className="text-[#8B91A8] text-xs mb-1 block">목표값</label>
                  <input type="number" value={newKPI.target_value} onChange={e => setNewKPI(f => ({ ...f, target_value: e.target.value }))}
                    placeholder="100"
                    className="w-full bg-[#252830] text-white text-xs px-3 py-2 rounded-lg border border-[#2A2D3E] focus:outline-none focus:border-[#A78BFA] placeholder-[#3E4155]" />
                </div>
                <div style={{ width: 60 }}>
                  <label className="text-[#8B91A8] text-xs mb-1 block">단위</label>
                  <input type="text" value={newKPI.unit} onChange={e => setNewKPI(f => ({ ...f, unit: e.target.value }))} placeholder="건"
                    className="w-full bg-[#252830] text-white text-xs px-2 py-2 rounded-lg border border-[#2A2D3E] focus:outline-none focus:border-[#A78BFA] placeholder-[#3E4155]" />
                </div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[#8B91A8] text-xs mb-1 block">카테고리 (자동 추적)</label>
                <select value={newKPI.category} onChange={e => setNewKPI(f => ({ ...f, category: e.target.value }))}
                  className="w-full bg-[#252830] text-white text-xs px-3 py-2 rounded-lg border border-[#2A2D3E] focus:outline-none cursor-pointer">
                  <option value="tasks">태스크 완료 수 (합계)</option>
                  <option value="capital">자본금 합계</option>
                  <option value="reputation">평균 직급점수</option>
                  <option value="checkins">출근 횟수 (합계)</option>
                  <option value="custom">기타</option>
                </select>
              </div>
              <div>
                <label className="text-[#8B91A8] text-xs mb-1 block">마감일 (선택)</label>
                <input type="date" value={newKPI.deadline} onChange={e => setNewKPI(f => ({ ...f, deadline: e.target.value }))}
                  className="w-full bg-[#252830] text-white text-xs px-3 py-2 rounded-lg border border-[#2A2D3E] focus:outline-none" />
              </div>
            </div>
            <div className="flex gap-2">
              <button type="button" onClick={() => setShowTeamKPIForm(false)}
                className="px-4 py-2 rounded-lg border border-[#2A2D3E] text-[#8B91A8] text-xs cursor-pointer hover:text-white transition-colors whitespace-nowrap">취소</button>
              <button type="button" onClick={handleAddTeamKPI} disabled={savingKPI || !newKPI.title.trim() || !newKPI.target_value}
                className="flex-1 py-2 rounded-lg bg-[#A78BFA] text-white text-xs font-semibold cursor-pointer hover:bg-[#B89DFF] transition-colors whitespace-nowrap disabled:opacity-50">
                {savingKPI ? "추가 중..." : "팀 KPI 등록"}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* 최종 발표 목록 */}
      <div className="flex gap-5" style={{ minHeight: 500 }}>
        <div className="flex flex-col shrink-0 space-y-2" style={{ width: 340 }}>
          <div className="grid grid-cols-3 gap-2 mb-2">
            {[["all","전체","#8B91A8",list.length],["submitted","검토 대기","#FF9F43",submittedCnt],["reviewed","평가 완료","#00C896",reviewedCnt]].map(([s, label, color, cnt]) => (
              <button key={s as string} type="button" onClick={() => setFilterStatus(s as "all" | "submitted" | "reviewed")}
                className="rounded-xl border px-2 py-2.5 text-center transition-all cursor-pointer"
                style={{ borderColor: filterStatus === s ? `${color}50` : "rgba(255,255,255,0.05)", backgroundColor: filterStatus === s ? `${color as string}12` : "#111318" }}>
                <div className="text-lg font-bold tabular-nums" style={{ color: color as string }}>{cnt as number}</div>
                <div className="text-[#5A5E72] text-[9px]">{label as string}</div>
              </button>
            ))}
          </div>

          {loading ? [...Array(3)].map((_, i) => <div key={i} className="h-16 bg-white/[0.02] rounded-xl animate-pulse border border-white/[0.04]" />) :
          filtered.length === 0 ? (
            <div className="text-center py-16"><i className="ri-presentation-line text-3xl text-[#222] block mb-2"></i><p className="text-[#3D3D3D] text-xs">제출된 발표가 없어요</p></div>
          ) : filtered.map(p => {
            const cfg = STATUS_CFG[p.status];
            return (
              <button key={p.id} type="button" onClick={() => { setSelected(p); setScore(p.admin_score ?? 4); setFeedback(p.admin_feedback ?? ""); }}
                className={`w-full text-left px-4 py-3 rounded-xl border transition-all cursor-pointer ${selected?.id === p.id ? "border-[#7A9BFF]/30 bg-[#7A9BFF]/[0.04]" : "border-white/[0.05] bg-[#111318] hover:border-white/[0.1]"}`}>
                <div className="flex items-center gap-2 mb-1">
                  <div className="w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold shrink-0 bg-[#7A9BFF]/15 text-[#7A9BFF]">{(p.display_name ?? "?")[0]?.toUpperCase()}</div>
                  <span className="text-[#CCCCCC] text-xs font-medium truncate flex-1">{p.display_name}</span>
                  <i className={`${cfg.icon} text-sm shrink-0`} style={{ color: cfg.color }}></i>
                </div>
                <p className="text-[#555555] text-[10px] truncate">{p.title}</p>
                {p.status === "reviewed" && p.admin_score && (
                  <p className="text-xs font-black mt-0.5" style={{ color: SCORES.find(s => s.value === p.admin_score)?.color }}>{SCORES.find(s => s.value === p.admin_score)?.grade}</p>
                )}
              </button>
            );
          })}
        </div>

        <div className="flex-1 bg-[#111318] rounded-xl border border-[#252830] flex flex-col overflow-hidden">
          {!selected ? (
            <div className="flex-1 flex flex-col items-center justify-center gap-3">
              <i className="ri-presentation-line text-3xl text-[#333] block"></i>
              <p className="text-[#5A5E72] text-sm">발표 자료를 선택하세요</p>
              {submittedCnt > 0 && <p className="text-[#FF9F43]/70 text-xs">검토 대기 {submittedCnt}건</p>}
            </div>
          ) : (
            <>
              <div className="px-5 py-4 border-b border-[#252830] flex items-center justify-between shrink-0">
                <div className="flex items-center gap-2.5">
                  <div className="w-8 h-8 rounded-full bg-[#7A9BFF]/15 text-[#7A9BFF] flex items-center justify-center text-xs font-bold">{(selected.display_name ?? "?")[0]?.toUpperCase()}</div>
                  <div><p className="text-white text-sm font-semibold">{selected.display_name}</p><p className="text-[#5A5E72] text-[10px]">{selected.email}</p></div>
                </div>
                <button type="button" onClick={() => setSelected(null)} className="w-7 h-7 flex items-center justify-center text-[#5A5E72] hover:text-white cursor-pointer transition-colors"><i className="ri-close-line text-base"></i></button>
              </div>

              <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3">
                {[
                  { label: "발표 제목", value: selected.title, icon: "ri-file-text-line" },
                  { label: "활동 요약", value: selected.summary, icon: "ri-newspaper-line" },
                  { label: "주요 성과", value: selected.achievements, icon: "ri-trophy-line" },
                  { label: "어려웠던 점", value: selected.challenges, icon: "ri-error-warning-line" },
                  { label: "배운 점 & 성장", value: selected.learnings, icon: "ri-lightbulb-line" },
                ].map(f => (
                  <div key={f.label} className="bg-[#1C1F2E] rounded-lg border border-[#252830] px-4 py-3">
                    <div className="text-[#5A5E72] text-[9px] uppercase tracking-widest mb-1.5 flex items-center gap-1"><i className={`${f.icon} text-xs`}></i>{f.label}</div>
                    <p className="text-[#CCCCCC] text-xs leading-relaxed whitespace-pre-wrap">{f.value}</p>
                  </div>
                ))}
              </div>

              {selected.status !== "reviewed" && (
                <div className="px-5 py-4 border-t border-[#252830] space-y-3 shrink-0">
                  <div>
                    <label className="text-[#8B91A8] text-xs mb-2 block">평가 점수</label>
                    <div className="flex gap-2">
                      {SCORES.map(s => (
                        <button key={s.value} type="button" onClick={() => setScore(s.value)}
                          className="flex-1 flex flex-col items-center py-2 rounded-xl border cursor-pointer transition-all"
                          style={{ borderColor: score === s.value ? `${s.color}50` : "rgba(255,255,255,0.06)", backgroundColor: score === s.value ? `${s.color}15` : "transparent" }}>
                          <span className="text-lg font-black" style={{ color: score === s.value ? s.color : "#3E4155" }}>{s.grade}</span>
                          <span className="text-[9px] text-[#5A5E72]">{s.label}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <label className="text-[#8B91A8] text-xs mb-1 block">피드백 코멘트 (선택)</label>
                    <textarea value={feedback} onChange={e => setFeedback(e.target.value)} rows={3} maxLength={500} placeholder="학생에게 전달할 구체적인 피드백을 남기세요"
                      className="w-full bg-[#1C1F2E] text-[#CCCCCC] text-xs px-3 py-2.5 rounded-lg border border-[#252830] focus:outline-none focus:border-[#7A9BFF]/40 placeholder-[#3E4155] resize-none" />
                  </div>
                  <button type="button" onClick={handleReview} disabled={saving}
                    className="w-full flex items-center justify-center gap-2 py-3 rounded-xl bg-[#7A9BFF] text-white text-sm font-bold cursor-pointer hover:bg-[#8AAEFF] transition-all whitespace-nowrap disabled:opacity-50">
                    <i className="ri-check-double-line text-base"></i>{saving ? "처리 중..." : "평가 완료 처리"}
                  </button>
                </div>
              )}
              {selected.status === "reviewed" && (
                <div className="px-5 py-4 border-t border-[#252830] shrink-0">
                  <div className="flex items-center gap-3 bg-[#00C896]/[0.07] border border-[#00C896]/20 rounded-xl px-4 py-3">
                    <span className="text-3xl font-black" style={{ color: SCORES.find(s => s.value === selected.admin_score)?.color }}>{SCORES.find(s => s.value === selected.admin_score)?.grade}</span>
                    <div><p className="text-[#00C896] text-xs font-semibold">평가 완료</p>{selected.admin_feedback && <p className="text-[#888] text-xs mt-0.5">{selected.admin_feedback}</p>}</div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
